import type { $Fetch, FetchError, FetchOptions, FetchRequest } from 'ofetch';
import { $fetch } from 'ofetch';
import { FETCH_TIMEOUT_MS } from '#layers/base/constants/fetch';
import { getErrorStatusCode } from './error-status';

export type FetchFunction = <
	DefaultT = unknown,
	DefaultR extends FetchRequest = FetchRequest,
	T = DefaultT,
	R extends FetchRequest = DefaultR,
	O extends FetchOptions = FetchOptions,
>(
	endpoint: R,
	options?: Omit<O, 'baseURL'>,
) => Promise<T>;

export interface ApiResponseErrorContext {
	request: FetchRequest;
	error?: Error;
	data: unknown;
}

type ApiResponseErrorHandler = (context: ApiResponseErrorContext) => void;

const responseErrorHandlers = new Set<ApiResponseErrorHandler>();

/** Returns an unregister function. */
export function onApiResponseError(handler: ApiResponseErrorHandler) {
	responseErrorHandlers.add(handler);

	return () => {
		responseErrorHandlers.delete(handler);
	};
}

/** Recovers the session after a 401. Resolves `true` when the request is worth replaying. */
type UnauthorizedHandler = () => Promise<boolean>;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** One slot, not a set: two recoveries would race the refresh-token rotation. */
export function setUnauthorizedHandler(handler: UnauthorizedHandler) {
	unauthorizedHandler = handler;

	return () => {
		if (unauthorizedHandler === handler) {
			unauthorizedHandler = null;
		}
	};
}

export interface ApiFetchOptions extends FetchOptions {
	/**
	 * Opt out of the unauthorized replay. The refresh endpoint recovery itself calls
	 * must stay out, or a failed refresh recurses through its own recovery.
	 */
	retryUnauthorized?: boolean;
}

function reportFailure(request: FetchRequest, error: Error | undefined, data: unknown) {
	console.error(`[fetch]: request ${request} failed with:`, error ?? data);

	for (const handler of responseErrorHandlers) {
		handler({ request, error, data });
	}
}

/** The single seam every API call goes through, so cross-cutting concerns attach once. */
export function createApiFetch({ retryUnauthorized = true, ...defaults }: ApiFetchOptions = {}): $Fetch {
	function willRetryUnauthorized(status: number) {
		return retryUnauthorized && status === 401 && unauthorizedHandler !== null;
	}

	const instance = $fetch.create({
		timeout: FETCH_TIMEOUT_MS,
		...defaults,
		onResponseError({ request, error, response }) {
			// A 401 the wrapper is about to recover from isn't worth reporting. The
			// wrapper reports it itself if recovery or the replay then fails, so this
			// has to stay silent rather than report twice.
			if (willRetryUnauthorized(response.status)) {
				return;
			}

			// Read the error off the parsed body: https://github.com/unjs/ofetch/issues/397
			reportFailure(request, error, response._data);
		},
	});

	/**
	 * `call` runs at most twice — once, then once more after a successful recovery —
	 * so a request can never retry more than one time however recovery goes.
	 */
	async function withUnauthorizedRetry<R>(request: FetchRequest, call: () => Promise<R>) {
		try {
			return await call();
		}
		catch (error) {
			if (!willRetryUnauthorized(getErrorStatusCode(error) ?? 0)) {
				throw error;
			}

			if (!await unauthorizedHandler!()) {
				reportFailure(request, error as Error, (error as FetchError).data);
				throw error;
			}

			try {
				return await call();
			}
			catch (replayError) {
				// Only a second suppressed 401 needs reporting; `onResponseError` already
				// reported anything else, and reporting again would double-fire handlers.
				if (willRetryUnauthorized(getErrorStatusCode(replayError) ?? 0)) {
					reportFailure(request, replayError as Error, (replayError as FetchError).data);
				}

				throw replayError;
			}
		}
	}

	const apiFetch = (request: FetchRequest, options?: FetchOptions) =>
		withUnauthorizedRetry(request, () => instance(request, options));

	return Object.assign(apiFetch, {
		// `raw` recovers too: `onResponseError` suppresses its 401 expecting a replay,
		// so leaving it bare would swallow the failure entirely.
		raw: (request: FetchRequest, options?: FetchOptions) =>
			withUnauthorizedRetry(request, () => instance.raw(request, options)),
		native: instance.native,
		// Derived instances keep the retry policy; copying `instance.create` would
		// hand back a fetch that silently skips it.
		create: (options?: ApiFetchOptions) => createApiFetch({ retryUnauthorized, ...defaults, ...options }),
	}) as $Fetch;
}
